/* eslint-disable complexity */
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useLocalization } from '@/data/Localization';
import { ButtonBase, ClickAwayListener, Divider, Stack, Typography } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { HeaderMiniCartDropMenuItem } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItem';
import ClearIcon from '@mui/icons-material/Clear';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { useOrderItemListRowsAvailability } from '@/data/Content/OrderItemInventory';
import { useNotifications } from '@/data/Content/Notifications';
import { useMobileHeaderQuery } from '..';
import { Img } from '@/components/blocks/MaterialImage';
import { useBasicCheckOut } from '@/data/Content/BasicCheckOut';
import { OrderSummaryBreakdown } from '@/components/content/Cart/parts/OrderSummaryBreakdown';

export const HeaderMiniCartDropMenu: FC<{
	fromLocal: boolean;
	onClose: () => void;
	// eslint-disable-next-line complexity
}> = ({ fromLocal, onClose }) => {
	const CartLabels = useLocalization('MiniCart');
	const RouteLabels = useLocalization('Routes');
	const { orderItems, data: order, checkout, canContinue, getCount, router } = useCart();
	const isMobile = useMobileHeaderQuery();
	const MAX_ROWS = isMobile ? 2 : 3;
	const { firstNRows } = useMemo(() => {
		const firstNRows = orderItems?.slice(0, MAX_ROWS);
		const firstNRowsCount = getCount(firstNRows);
		return { firstNRows, firstNRowsCount };
	}, [MAX_ROWS, getCount, orderItems]);

	const { totalCoupons, totalDiscount, totalPriceWithoutOffer, orderItemListRowsSorted } =
		useBasicCheckOut({
			adjustment: order?.adjustment || [],
			orderItem: order?.orderItem || [],
			orderId: order?.orderId || '',
		});

	const { grandTotal = '0', totalShippingCharge = '0' } = order || {};

	const { showSuccessMessage } = useNotifications();
	const {
		orderItemListRowsWithAvailability,
		orderItemListRowsNotAvailable,
		loadingOrderItemListRowsAvailability,
	} = useOrderItemListRowsAvailability(orderItemListRowsSorted);

	const loading =
		orderItemListRowsWithAvailability.length === 0 || loadingOrderItemListRowsAvailability;

	const disabled = loading ? true : canContinue ? !canContinue() : false;

	const handleOnClickCheckout = () => {
		if (orderItemListRowsNotAvailable.length > 0) {
			router.push(RouteLabels.Cart.route.t()).finally(() => {
				showSuccessMessage(
					'Revisa tus productos del carrito y elimina el producto caducado para continuar tu compra.',
					'TIENES UN PRODUCTO CADUCADO'
				);
			});

			return;
		}

		checkout();
	};

	const handleOnClickCart = () => {
		const path = window.location.pathname;
		if (path.includes(RouteLabels.Cart.route.t())) {
			onClose();
			return;
		}
		router.push(RouteLabels.Cart.route.t());
	};

	return (
		<ClickAwayListener onClickAway={onClose}>
			<Stack
				sx={{
					width: '290px',
					padding: 'var(--spacing-5)',
					backgroundColor: '#FFFFFF',
					borderRadius: '0px 0px 6px 6px',
					position: 'relative',
				}}
			>
				{firstNRows?.length > 0 ? (
					<Stack>
						<Stack
							direction="row"
							sx={{
								justifyContent: 'space-between',
								alignItems: 'center',
								marginBottom: 'var(--spacing-5)',
							}}
						>
							<Typography
								sx={{
									fontSize: 'var(--font-size-small)',
									color: '#2C2C2C',
									display: 'flex',
									alignItems: 'center',
									textTransform: 'uppercase',
									fontWeight: 800,
								}}
							>
								{!fromLocal ? (
									<Img
										src="/images/check-circle.png"
										alt="check-circle"
										sx={{ height: '16px', width: '16px', marginRight: '8px' }}
									/>
								) : null}
								{fromLocal ? 'Carrito de compras' : 'Agregaste al carrito'}
							</Typography>
							<ClearIcon
								htmlColor="#929292"
								sx={{ fontSize: 'var(--font-size-body)', cursor: 'pointer', marginRight: '-6px' }}
								onClick={onClose}
							/>
						</Stack>
						<Stack
							spacing={1}
							sx={{
								maxHeight: 250,
								overflowY: 'auto',
								'&::-webkit-scrollbar': {
									width: '4px',
									backgroundColor: '#f0f0f0',
								},
								'&::-webkit-scrollbar-thumb': {
									backgroundColor: '#888',
									borderRadius: '10px',
								},
							}}
						>
							{!loading ? (
								<React.Fragment>
									{orderItemListRowsWithAvailability.map((product) => (
										<HeaderMiniCartDropMenuItem
											key={product.orderItemListRow.orderItem.orderItemId}
											thumbnail={product.details.thumbnail}
											name={product.details.name}
											attributes={product.details.attributes}
											offerPrice={product.details.prices.offer || 0}
											displayPrice={product.details.prices.list || 0}
											totalAdjustment={product.orderItemListRow.totalAdjustment.totalAdjustment}
											orderItemPrice={product.orderItemListRow.price.orderItemPrice}
											exhausted={product.availability?.status === false}
											href={product.details.seo?.href}
										/>
									))}
								</React.Fragment>
							) : (
								<HeaderMiniCartDropMenuItemSkeleton />
							)}
						</Stack>
						<Divider sx={{ marginTop: 'var(--spacing-5)', marginBottom: '11px' }} />
						<Typography
							component="span"
							sx={{
								fontSize: 'var(--font-size-small)',
								marginBottom: '11px',
							}}
						>
							{'Hay '}
							<Typography
								component="span"
								sx={{
									fontWeight: 800,
								}}
							>
								{orderItemListRowsWithAvailability.length}
							</Typography>
							{' productos en tu carrito'}
						</Typography>
						<OrderSummaryBreakdown
							showTitle={false}
							total={grandTotal}
							shipping={totalShippingCharge}
							discount={totalDiscount}
							subtotal={totalPriceWithoutOffer}
							coupons={totalCoupons}
							isMiniCart
						/>
						<Stack
							direction="row"
							spacing="14px"
							justifyContent="center"
							alignItems="center"
							sx={{
								marginTop: '30px',
							}}
						>
							<ButtonBase
								id="mini-cart-view-full-cart-button"
								data-testid="mini-cart-view-full-cart-button"
								sx={{
									backgroundColor: '#F1F1F1',
									color: '#2C2C2C',
									borderRadius: 50,
									height: '38px',
									paddingX: 'var(--spacing-5)',
									fontWeight: 600,
									fontSize: 'var(--font-size-body)',
								}}
								onClick={handleOnClickCart}
							>
								{'Ver carrito'}
							</ButtonBase>
							<ButtonBase
								sx={{
									backgroundColor: '#2682E7',
									color: '#FFFFFF',
									borderRadius: 50,
									height: '38px',
									paddingX: 'var(--spacing-5)',
									fontWeight: 600,
									fontSize: 'var(--font-size-body)',
									':disabled': {
										backgroundColor: '#B5B5B5',
										color: '#FFFFFF',
									},
								}}
								disabled={disabled}
								onClick={handleOnClickCheckout}
							>
								{'Ir a pagar'}
							</ButtonBase>
						</Stack>
					</Stack>
				) : (
					<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
						<Typography
							variant="body1"
							align="center"
							sx={{
								fontSize: 'var(--font-size-small)',
							}}
						>
							{CartLabels.Empty.t()}
						</Typography>
						<ClearIcon
							sx={{
								fontSize: 'var(--font-size-body)',
								cursor: 'pointer',
								marginRight: '-6px',
								color: 'rgb(146, 146, 146)',
								position: 'absolute',
								top: '7px',
								right: '15px',
							}}
							onClick={onClose}
						/>
					</Stack>
				)}
			</Stack>
		</ClickAwayListener>
	);
};
